import { PreparedLane } from '@/models/organization.models';
import { FindTeamsQueryParams } from '@/models/user.models';
import { organizationData } from '@/store/helpers';
import { computed, ComputedRef, watch, Ref } from 'vue';
import uniqBy from 'lodash-es/uniqBy';
import uniq from 'lodash-es/uniq';
import { useUserStore } from '@/store/userStore';
import { useBoardStore } from '@/store/boardStore';
import { LaneCollection, LaneCollectionLane, LaneType } from '@/api';

export function useTeamsQuery({
  query,
}: {
  query?: ComputedRef<FindTeamsQueryParams>;
} = {}) {
  const filteredTeams = computed(() => {
    const userStore = useUserStore();
    let filteredCacheTeams = userStore.teamCache;
    if (!query?.value) return filteredCacheTeams;

    if (query.value.q) {
      filteredCacheTeams = filteredCacheTeams.filter((t) =>
        t.name.toLowerCase().includes(query.value.q!.toLowerCase())
      );
    }

    // by default we never show archived teams
    if (!query?.value.archived) {
      filteredCacheTeams = filteredCacheTeams.filter((t) => !t.archived);
    }

    return filteredCacheTeams.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  });

  return {
    filteredTeams,
  };
}

export function useLanes({
  boardIds,
  laneCollectionIds,
}: {
  boardIds?: ComputedRef<Array<string>> | Ref<Array<string>>;
  laneCollectionIds?: ComputedRef<LaneCollection['id'][]>;
} = {}) {
  const { organization, boardCache } = organizationData();
  const boardStore = useBoardStore();

  const cachedBoards = computed(() => {
    if (!boardIds?.value?.length) return [];
    return boardIds?.value?.map((id) =>
      boardCache.value.find((b) => b.id === id)
    );
  });

  function getLaneCollection(laneCollectionId: string): Array<PreparedLane> {
    const collection = organization.value?.laneCollections.find(
      (lc) => lc.id === laneCollectionId
    );
    if (!collection) return [];
    const fullyPreparedLanes: Array<PreparedLane> = [];
    collection.lanes.forEach((lane: LaneCollectionLane) => {
      const fullLane = organization.value?.lanes.find(
        (l) => l.id === lane.laneId
      );
      if (fullLane) {
        fullyPreparedLanes.push({
          ...fullLane,
          wipLimit: lane.wipLimit,
          workTypeIds: lane.workTypeIds,
        });
      }
    });
    return fullyPreparedLanes;
  }

  const allLanes = computed(() => {
    if (laneCollectionIds?.value?.length) {
      let allLanes: Array<PreparedLane> = [];
      laneCollectionIds?.value?.forEach((id) => {
        const collectionLanes = getLaneCollection(id);
        allLanes = allLanes.concat(collectionLanes);
      });
      return allLanes;
    } else if (!boardIds?.value?.length) {
      let allLaneIdsOrderedByCollection: Array<string> = [];
      organization.value?.laneCollections.forEach((lc) => {
        allLaneIdsOrderedByCollection = [
          ...allLaneIdsOrderedByCollection,
          ...lc.lanes.map((l) => l.laneId),
        ];
      });
      const middleLanes = uniq(allLaneIdsOrderedByCollection)
        .map((laneId) => organization.value?.lanes.find((l) => l.id === laneId))
        .filter((l) => !!l && l.type === LaneType.CUSTOM);
      return [
        organization.value?.lanes.find((l) => l.type === LaneType.NEW),
        ...middleLanes,
        organization.value?.lanes.find((l) => l.type === LaneType.DONE),
      ].filter((l) => !!l) as Array<PreparedLane>;
    } else {
      let allLanes: Array<PreparedLane> = [];

      cachedBoards.value
        .filter((b) => !!b)
        .forEach((b) => {
          if (!b?.laneCollectionId) return;
          const collectionLanes = getLaneCollection(b?.laneCollectionId);
          allLanes = allLanes.concat(collectionLanes);
        });
      allLanes = uniqBy(
        allLanes.filter((l) => l.type === LaneType.CUSTOM),
        'id'
      );
      return [
        organization.value?.lanes.find((l) => l.type === LaneType.NEW),
        ...allLanes,
        organization.value?.lanes.find((l) => l.type === LaneType.DONE),
      ].filter((l) => !!l) as Array<PreparedLane>;
    }
  });

  watch(
    () => boardIds?.value,
    () => {
      boardIds?.value?.forEach((id) => {
        if (!boardCache.value.find((b) => b.id === id)) {
          boardStore.getBoard(id);
        }
      });
    },
    { immediate: true }
  );

  return {
    getLaneCollection,
    allLanes,
  };
}
